import './AgencyHubPage.scss';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { PROJECTS } from '../../shared/routes/path';
import Title from '../../shared/components/title/Title';
import useAddon from '../../shared/hooks/global/UseAddon';
import useIsMobile from '../../shared/hooks/global/UseIsMobile';
import MobilePageHeader from '../../shared/components/common/mobile-menu/MobilePageHeader';
import { useAccount } from '../../shared/contexts/account/AccountContexts';
import { isAdminOrOwner } from '../../shared/utils/utils';
import { getTeam } from '../../shared/services/team/TeamService';
import { ownProjects } from './agencyHubRows';
import { AGENCY_HUB_TEAM_QUERY } from './agencyHubQueryKeys';
import type { TeamResponse } from '../../shared/models/team/TeamResponse';
import UsageTiles, { UsageTileData } from './components/usage-tiles/UsageTiles';
import ProjectsSection from './components/projects-section/ProjectsSection';
import TeamSection from './components/team-section/TeamSection';
import ModelsTile from './components/models-tile/ModelsTile';

// A stable empty list: a fresh `[]` default on every render would re-run the team section's `[team]`
// effect and wipe its local edits while the query is disabled or loading.
const NO_TEAM: TeamResponse[] = [];

/**
 * Agency Hub — the agency's central view (prompt allocation, projects, client access). Reads are live:
 * the tiles from the account data (+ the team list for the active / pending split), the projects
 * section from `/projects/page`, the team section from `/team/data`. Writes wait for their endpoints
 * (`.dev/agency-summary.md` §15) and meanwhile edit local copies.
 *
 * The gate is the account's `AGENCY_HUB` addon plus the OWNER/ADMIN role: USER never sees the hub,
 * GUEST is already cut off by the route whitelist. No superadmin bypass on purpose — impersonation
 * hands out the owner's token, so a superadmin sees the hub exactly where the client does.
 */
const AgencyHubPage: React.FC = () => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const hasAgencyHubAddon = useAddon('AGENCY_HUB');
  const { accountResponse, projectResponse } = useAccount();
  const hasAccess = hasAgencyHubAddon && !!accountResponse && isAdminOrOwner(accountResponse.role);

  const { data: team = NO_TEAM } = useQuery({
    queryKey: [AGENCY_HUB_TEAM_QUERY],
    queryFn: getTeam,
    enabled: hasAccess,
    // Always refetch on mount: the invite page and the settings' team page change the list and come
    // back here; the cached copy paints first, then the fresh one. No focus refetch — the team section
    // keeps local (not yet persisted) edits until the next fetch.
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  // The account's own projects (no demo, none of a superadmin's other accounts): the projects count
  // and the names the team section needs.
  const accountProjects = useMemo(
    () => ownProjects(accountResponse, projectResponse ?? []),
    [accountResponse, projectResponse]
  );

  const tiles = useMemo<UsageTileData[]>(() => {
    if (!accountResponse) return [];
    return [
      {
        key: 'prompts',
        used: accountResponse.allocatedMonitorCount,
        max: accountResponse.maxMonitorCount,
        noteKey: 'agencyHubAllocatedAcrossProjects',
        upgradeCta: true,
      },
      {
        key: 'team',
        used: accountResponse.usersOnAccount,
        max: accountResponse.maxUsers,
        noteKey: 'agencyHubTeamNote',
        // active + pending = every seat (an unaccepted invite holds its seat until removed).
        noteOptions: {
          active: team.filter((member) => member.isActive).length,
          pending: team.filter((member) => !member.isActive).length,
        },
      },
      { key: 'projects', used: accountProjects.length, max: accountResponse.maxProjects },
    ];
  }, [accountResponse, team, accountProjects]);

  if (!hasAccess || !accountResponse) {
    return <Navigate to={PROJECTS} replace />;
  }

  return (
    <div className="agency-hub-page page-content">
      <div className="page-box">
        {/* Same header as Insights: title in the mobile bar, sticky title row with the subtitle on desktop. */}
        <MobilePageHeader title={<span>{t('agencyHub')}</span>} />
        {!isMobile && (
          <div className="page-box__filters" data-observe-sticky>
            <div className="agency-hub-page__inner">
              <Title
                title={t('agencyHub')}
                childrenOnMain={<div className="title-box__subtitle">{t('agencyHubSubtitle')}</div>}
              />
            </div>
          </div>
        )}
        <div className="page-box__stats agency-hub-page__inner">
          <div className="stats stats--full-width stats--padding-top">
            <UsageTiles tiles={tiles}>
              <ModelsTile features={accountResponse.features} />
            </UsageTiles>
          </div>
          <div className="stats stats--full-width">
            <ProjectsSection pool={accountResponse.maxMonitorCount} />
          </div>
          <div className="stats stats--full-width">
            <TeamSection
              team={team}
              projects={accountProjects.map((project) => ({
                id: project.id,
                name: project.projectLabel || project.firmName,
              }))}
              currentUserId={accountResponse.userId}
              maxUsers={accountResponse.maxUsers}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencyHubPage;
